'use client';
import {
  AspectRatio,
  Button,
  Grid,
  Stack,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { useI18nLn } from '~/hooks/store';
import { usePrizeTickets } from '~/src/services/arcApi/prize_tickets';
import { UnknownErrorContext } from '../common/UnknownErrorContent';
import { Skeleton, SkeletonText } from '../ui/skeleton';
import { PrizeTicketList } from './PrizeTicketList';
import { PrizeTicketUnAvailableModal } from './PrizeTicketUnAvailableModal';
import { PrizeTicketUseModal } from './PrizeTicketUseModal';

type PrizeTicketContainerProps = {
  gachaID?: string;
};

export const PrizeTicketContainer: React.FC<PrizeTicketContainerProps> = (
  props: PrizeTicketContainerProps
) => {
  const { t } = useI18nLn();
  const { gachaID } = props;
  const { prizeTickets, error, isLoading, mutate } = usePrizeTickets(
    gachaID ?? undefined
  );
  const prizeTicketUseModalDisclosure = useDisclosure();
  const unAvailablePrizeTicketDisclosure = useDisclosure();
  const [selectedTicketId, setSelectedTicketId] = useState<string>('');

  const availablePrizeTickets = useMemo(
    () =>
      prizeTickets
        .filter(
          (prizeTicket) =>
            prizeTicket.used_at == null &&
            (prizeTicket.expired_at == null ||
              (prizeTicket.expired_at &&
                new Date(prizeTicket.expired_at) >= new Date()))
        )
        .sort((a, b) => {
          return a.ticket_id < b.ticket_id ? 1 : -1;
        }),
    [prizeTickets]
  );

  const unAvailablePrizeTickets = useMemo(
    () =>
      prizeTickets
        .filter(
          (prizeTicket) =>
            prizeTicket.used_at != null ||
            (prizeTicket.expired_at != null &&
              new Date(prizeTicket.expired_at) < new Date())
        )
        .sort((a, b) => {
          const getInEffectiveDate = (prizeTicket): Date => {
            return prizeTicket.used_at ?? prizeTicket.expired_at ?? new Date();
          };
          return getInEffectiveDate(a) < getInEffectiveDate(b) ? 1 : -1;
        }),
    [prizeTickets]
  );

  if (isLoading) return <PrizeTicketListSkeleton />;
  if (error)
    return <UnknownErrorContext title="チケット情報の取得に失敗しました" />;

  return (
    <VStack w="full" gap="16px">
      <PrizeTicketList
        availablePrizeTickets={availablePrizeTickets}
        onOpen={prizeTicketUseModalDisclosure.onOpen}
        setSelectedTicketId={setSelectedTicketId}
      />
      <PrizeTicketUseModal
        mutate={mutate}
        availableTickets={availablePrizeTickets}
        selectedTicketId={selectedTicketId}
        ticketUseModalDisclosure={prizeTicketUseModalDisclosure}
      />
      <PrizeTicketUnAvailableModal
        unAvailableTickets={unAvailablePrizeTickets}
        usedTicketsDisclosure={unAvailablePrizeTicketDisclosure}
      />
      {availablePrizeTickets.length < 1 && (
        <Text fontSize="12px" textAlign="center" overflowWrap="break-word">
          {t('pages.prize_ticket.no_available_ticket')}
        </Text>
      )}
      <Button
        w="full"
        rounded="50px"
        fontSize="12px"
        fontWeight="bold"
        variant="outline"
        onClick={unAvailablePrizeTicketDisclosure.onOpen}
        disabled={unAvailablePrizeTickets.length < 1}
        color={unAvailablePrizeTickets.length >= 1 ? 'primary' : 'teal'}
      >
        {t('pages.prize_ticket.used_ticket')}
      </Button>
    </VStack>
  );
};

const PrizeTicketListSkeleton: React.FC = () => {
  return (
    <VStack w="full" m="16px" gap="16px">
      <Stack w="full" px="16px" align="center">
        <SkeletonText height="36px" lineClamp={1} w="240px" />
      </Stack>
      <Stack w="full" p="16px" bgColor="blank">
        <Grid templateColumns="repeat(3, 1fr)" gap="8px" mx="8px">
          {[...Array(8)].map((_, index) => {
            return (
              <AspectRatio ratio={1} key={index}>
                <Skeleton h="full" w="full" />
              </AspectRatio>
            );
          })}
        </Grid>
      </Stack>
      <SkeletonText w="full" height="42px" mt="16px" lineClamp={1} />
      <SkeletonText w="full" height="42px" lineClamp={1} />
    </VStack>
  );
};
